// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import DocumentButton from "../../../components/DocumentButton";

/**
 * ClientInfo component for displaying client details.
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Information title
 * @param {string} props.description - Information description
 * @returns {JSX.Element} Client information display
 */
const ClientInfo = ({ title, description }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
      {title}
    </MDTypography>
    <MDTypography variant="caption">{description}</MDTypography>
  </MDBox>
);

/**
 * Get initials from a full name.
 * 
 * @param {string} name - Full name
 * @returns {string} Initials (e.g., "JD" for "John Doe")
 */
function getInitials(name) {
  if (!name) return '';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Create a client row entry for the table.
 * 
 * @param {Object} client - Client object
 * @param {string} type - Client type ('individual' or 'entity')
 * @param {Array} templateFolders - Template folders
 * @param {Function} onClientClick - Callback when client name is clicked
 * @returns {Object} Table row object
 */
function createClientRow(client, type, templateFolders, onClientClick) {
  const title = type === 'individual' ? 'Individual' : 'Entity';
  const description = type === 'individual' ? 'Personal Account' : 'Corporate Account';
  
  return {
    client: (
      <MDBox display="flex" alignItems="center" lineHeight={1}>
        <MDAvatar bgColor="info" size="sm">
          {getInitials(client.full_name)}
        </MDAvatar>
        <MDBox ml={2} lineHeight={1}>
          <MDTypography 
            display="block" 
            variant="button" 
            fontWeight="medium"
            onClick={() => onClientClick && onClientClick(client)}
            sx={{ 
              cursor: onClientClick ? 'pointer' : 'default',
              '&:hover': onClientClick ? { textDecoration: 'underline' } : {}
            }}
          >
            {client.full_name}
          </MDTypography>
          <MDTypography variant="caption">{client.email}</MDTypography>
        </MDBox>
      </MDBox>
    ),
    info: <ClientInfo title={title} description={description} />,
    status: (
      <MDBox ml={-1}>
        <MDBadge badgeContent={client.status || "active"} color="success" variant="gradient" size="sm" />
      </MDBox>
    ),
    actions: (
      <MDBox display="flex" justifyContent="center" gap={1}>
        <DocumentButton 
          clientId={client.id} 
          clientName={client.full_name}
          clientType={type}
          templateFolders={templateFolders}
        />
      </MDBox>
    ),
    type,
    searchName: client.full_name,
    searchEmail: client.email
  };
}

/**
 * Returns table columns and rows for the clients table.
 * @param {Array} clients - List of client objects with type 'individual' or 'entity'.
 * @returns {Object} Table columns and rows for DataTable.
 */
/**
 * Generate table data for clients display.
 * 
 * @param {Array} [clients=[]] - Array of client objects
 * @param {Array} [templateFolders=[]] - Array of template folders
 * @param {Function} [onClientClick] - Callback function when client name is clicked
 * @returns {Object} Table data with columns and rows
 */
export default function clientsTableData(clients = [], templateFolders = [], onClientClick = null) {
  const individualRows = clients
    .filter(c => c.type === 'individual')
    .map(client => createClientRow(client, 'individual', templateFolders, onClientClick));

  const entityRows = clients
    .filter(c => c.type === 'entity')
    .map(client => createClientRow(client, 'entity', templateFolders, onClientClick));

  return {
    columns: [
      { Header: "client", accessor: "client", width: "35%", align: "left" },
      { Header: "info", accessor: "info", align: "left" },
      { Header: "status", accessor: "status", align: "center" },
      { Header: "actions", accessor: "actions", align: "center" },
    ],
    rows: [...individualRows, ...entityRows],
  };
} 